import { SidUserClient } from "@sid/sid_client";
import { BaseInfo, CommonAttrInfo, LinkageUserData, ManageInfo, MemberAgreementInfo, ShippingInfo, SiteInfo, UserBase, UserShueishaPublication } from "@/commons/interfaces";
import { InjectionKey, reactive } from "vue";
import { LinkageUserItem } from "@/modules/linkage/linkageUserItem";
import ShueishaPublicationProfile from "@/components/Linkage/Sites/ShueishaPublicationProfile.vue";
import ShueishaPublicationConfirm from "@/components/Linkage/Sites/ShueishaPublicationConfirm.vue";
import ShueishaPublicationLoginCustom from "@/components/Linkage/Sites/ShueishaPublicationLoginCustom.vue";

// 集英社書籍関連定数定義
/** 連携ID */
export const SHUEISHA_PUBLICATION_LINKAGE_ID = 14;
/** 連携名称 */
export const SHUEISHA_PUBLICATION_LINKAGE_NAME = "ShueishaPublication";
/** 連携パス */
export const SHUEISHA_PUBLICATION_PATH = "shueisha_publication";
/** サイト固有情報APIパス */
export const SHUEISHA_PUBLICATION_SITE_UNIQ_API_PATH = "/shueisha_publication/me";
/** デフォルトコールバックURL */
export const SHUEISHA_PUBLICATION_DEFAULT_CALLBACK_URL = process.env.VUE_APP_SHUEISHA_PUBLICATION_DEFAULT_CALLBACK_URL;
/** 許容コールバックURL */
export const SHUEISHA_PUBLICATION_ACCEPT_CALLBACK_URLS = process.env.VUE_APP_SHUEISHA_PUBLICATION_ACCEPT_CALLBACK_URLS.split(",").map((url: string) => `^${url}(\\?.*)?$`);
/** サービス規約URL */
export const SHUEISHA_PUBLICATION_SERVICE_TERM_URL = process.env.VUE_APP_SHUEISHA_PUBLICATION_SERVICE_TERM_URL;
/** メールアドレスドメイン */
export const SHUEISHA_PUBLICATION_EMAIL_DOMAINS = [
    "bungei.sid.shueisha.co.jp",
];
/** 基本属性情報入力 */
export const SHUEISHA_PUBLICATION_PROFILE_COMPONENT = ShueishaPublicationProfile;
/** 基本属性情報確認 */
export const SHUEISHA_PUBLICATION_CONFIRM_COMPONENT = ShueishaPublicationConfirm;
/** コールバックURLの任意指定 */
export const SHUEISHA_PUBLICATION_IS_CALLBACK_OPTIONAL = true;

/**
 * 集英社書籍連携画面用基本属性情報管理クラス
 */
export class ShueishaPublicationUserBase implements LinkageUserData<UserBase> {

    /** インジェクションキー */
    public static injectionKey: InjectionKey<LinkageUserItem<LinkageUserData<UserBase>>> = Symbol("Shueisha_PublicationUserBase");

    /** 基本属性情報 */
    public readonly data: UserBase = reactive(new UserBase());
    /**
     * @inheritDoc LinkageUserData
     */
    public initData(): void {
        this.copy(new UserBase());
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public getData(): UserBase {
        return this.data;
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public copy(obj: UserBase): void {
        this.data.identityId = obj.identityId;
        this.data.userName = obj.userName;
        this.data.baseInfo = Object.assign(new BaseInfo(), obj.baseInfo);
        this.data.shippingInfo = obj.shippingInfo?.map(inf => Object.assign(new ShippingInfo(), inf)) || [];
        this.data.siteInfo = obj.siteInfo?.map(inf => Object.assign(new SiteInfo(), inf)) || [];
        this.data.commonAttrInfo = Object.assign(new CommonAttrInfo(), obj.commonAttrInfo);
        this.data.memberAgreeInfo = Object.assign(new MemberAgreementInfo(), obj.memberAgreeInfo);
        this.data.manageInfo = Object.assign(new ManageInfo(), obj.manageInfo);
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public async fetch(sidClient: SidUserClient): Promise<void> {
        const response = await sidClient.getUserAttr()
            .then(res => res.data)
            .catch(res => {
                if (res.response?.status === 404) {
                    return new UserBase();
                } else {
                    throw new Error("Failed to get information.");
                }
            });
        this.copy(response);
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public checkRequired(): boolean {
        // 基本属性情報の有無
        if (!this.data.identityId) return false;
        // 登録者情報
        // if (!this.data.baseInfo.firstName) return false;
        // if (!this.data.baseInfo.lastName) return false;
        // if (!this.data.baseInfo.firstKana) return false;
        // if (!this.data.baseInfo.lastKana) return false;
        // if (!this.data.baseInfo.zipCode) return false;
        // if (!this.data.baseInfo.prefecture) return false;
        // if (!this.data.baseInfo.address) return false;
        // if (!this.data.baseInfo.building) return false;
        if (!this.data.baseInfo.email) return false;
        // if (!this.data.baseInfo.tel) return false;
        // 属性情報
        if (!this.data.commonAttrInfo.gender) return false;
        if (!this.data.commonAttrInfo.birthday) return false;
        // if (!this.data.commonAttrInfo.imagePath) return false;
        // if (!this.data.commonAttrInfo.nickName) return false;
        // if (this.data.commonAttrInfo.isMarried === null || this.data.commonAttrInfo.isMarried === undefined) return false;
        // if (this.data.commonAttrInfo.hasChild === null || this.data.commonAttrInfo.hasChild === undefined) return false;
        // if (!this.data.commonAttrInfo.bloodType) return false;
        return true;
    }

    /**
     * @inheritDoc LinkageUserData
     */
    getInjectKey(): InjectionKey<LinkageUserItem<LinkageUserData<UserBase>>> {
        return ShueishaPublicationUserBase.injectionKey;
    }

}

/**
 * 集英社書籍連携画面用サイト固有情報管理クラス
 */
export class ShueishaPublicationSiteUniq implements LinkageUserData<UserShueishaPublication> {

    /** インジェクションキー */
    public static injectionKey: InjectionKey<LinkageUserItem<LinkageUserData<UserShueishaPublication>>> = Symbol("UserShueishaPublication");

    /** 集英社書籍サイト固有情報 */
    public readonly data: UserShueishaPublication = reactive(new UserShueishaPublication());

    /**
     * @inheritDoc LinkageUserData
     */
    public initData(): void {
        this.copy(new UserShueishaPublication());
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public getData(): UserShueishaPublication {
        return this.data;
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public copy(obj: UserShueishaPublication): void {
        this.data.identityId = obj.identityId;
        this.data.userName = obj.userName;
        this.data.isSendMailMagazine = obj.isSendMailMagazine;
        this.data.manageInfo = Object.assign({}, obj.manageInfo);
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public async fetch(sidClient: SidUserClient): Promise<void> {
        const response = await sidClient.requestApi({ method: "GET", path: SHUEISHA_PUBLICATION_SITE_UNIQ_API_PATH })
            .then(res => res.data)
            .catch(res => {
                if (res.response?.status === 404) {
                    return new UserShueishaPublication();
                } else {
                    throw new Error("Failed to get information.");
                }
            });
        this.copy(response);
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public checkRequired(): boolean {
        // サイト固有情報の有無
        if (this.data.isSendMailMagazine === null || this.data.isSendMailMagazine === undefined) return false;
        return true;
    }

    /**
     * @inheritDoc LinkageUserData
     */
    getInjectKey(): InjectionKey<LinkageUserItem<LinkageUserData<UserShueishaPublication>>> {
        return ShueishaPublicationSiteUniq.injectionKey;
    }
}